import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { 
  TrophyIcon, 
  ChartBarIcon, 
  UserGroupIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

const RoleBasedContent = () => {
  const { userRole, isFounder, isInvestor, isClient } = useAuth();

  const getRoleContent = () => {
    if (isFounder()) {
      return {
        icon: TrophyIcon,
        title: 'Founder Dashboard',
        description: 'Full platform access with administrative privileges',
        features: [
          'Complete analytics suite',
          'User management',
          'System configuration',
          'All client data access',
          'Platform metrics'
        ],
        color: 'from-accent-gold to-yellow-500',
        bgColor: 'bg-accent-gold/10',
        borderColor: 'border-accent-gold/30'
      };
    }
    
    if (isInvestor()) {
      return {
        icon: ChartBarIcon,
        title: 'Investor Portal',
        description: 'Advanced analytics and market insights',
        features: [
          'Market analysis tools',
          'Performance dashboards',
          'Investment metrics',
          'Portfolio insights',
          'Research reports'
        ],
        color: 'from-accent-blue to-blue-500',
        bgColor: 'bg-accent-blue/10',
        borderColor: 'border-accent-blue/30'
      };
    }
    
    if (isClient()) {
      return {
        icon: UserGroupIcon,
        title: 'Client Services',
        description: 'Personalized liquidity consulting and support',
        features: [
          'Personal consultation',
          'Custom reports',
          'Token analysis',
          'Strategy recommendations',
          'Support tickets'
        ],
        color: 'from-green-400 to-emerald-500',
        bgColor: 'bg-green-400/10',
        borderColor: 'border-green-400/30'
      };
    }

    return {
      icon: ExclamationTriangleIcon,
      title: 'Access Error',
      description: 'Unable to determine user role',
      features: ['Please contact support'],
      color: 'from-red-400 to-red-600',
      bgColor: 'bg-red-400/10',
      borderColor: 'border-red-400/30'
    };
  };

  const content = getRoleContent();
  const IconComponent = content.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`glass-card p-6 ${content.bgColor} border ${content.borderColor}`}
    >
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <div className={`w-12 h-12 bg-gradient-to-br ${content.color} rounded-xl flex items-center justify-center`}>
          <IconComponent className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-heading-md font-serif font-bold text-cream-100">
            {content.title}
          </h3>
          <p className="text-cream-300 text-sm">
            Role: {userRole?.charAt(0).toUpperCase() + userRole?.slice(1)}
          </p>
        </div>
      </div>

      {/* Description */}
      <p className="text-cream-200 mb-6 leading-relaxed">
        {content.description}
      </p>

      {/* Features */}
      <div>
        <h4 className="text-cream-100 font-medium mb-3">Available Features:</h4>
        <ul className="space-y-2">
          {content.features.map((feature, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="flex items-center text-cream-300 text-sm"
            >
              <div className={`w-1.5 h-1.5 bg-gradient-to-r ${content.color} rounded-full mr-3`} />
              {feature}
            </motion.li>
          ))}
        </ul>
      </div>

      {/* Role-specific Actions */}
      <div className="mt-6 pt-6 border-t border-white/10">
        {isFounder() && (
          <div className="space-y-2">
            <button className="w-full bg-accent-gold/20 hover:bg-accent-gold/30 text-accent-gold border border-accent-gold/40 rounded-lg py-2 px-4 text-sm font-medium transition-all duration-200">
              Access Admin Panel
            </button>
          </div>
        )}
        
        {isInvestor() && (
          <div className="space-y-2">
            <button className="w-full bg-accent-blue/20 hover:bg-accent-blue/30 text-accent-blue border border-accent-blue/40 rounded-lg py-2 px-4 text-sm font-medium transition-all duration-200">
              View Investment Dashboard
            </button>
          </div>
        )}
        
        {isClient() && (
          <div className="space-y-2">
            <button className="w-full bg-green-400/20 hover:bg-green-400/30 text-green-400 border border-green-400/40 rounded-lg py-2 px-4 text-sm font-medium transition-all duration-200">
              Schedule Consultation
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RoleBasedContent;
